import React, { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';

// List of common spam keywords and patterns
const SPAM_KEYWORDS = [
  'casino', 'loan', 'bitcoin', 'crypto', 'investment', 'forex',
  'make money', 'earn money', 'work from home', 'get rich', 'quick cash',
  'lottery', 'prize', 'winner', 'inheritance', 'million dollar',
  'viagra', 'cialis', 'pharmacy', 'medication', 'pills',
  'dating', 'singles', 'meet singles', 'hot girls', 'sexy',
  'nigerian prince', 'bank transfer', 'western union', 'wire transfer',
  'urgent', 'confidential', 'opportunity', 'limited time', 'act now',
  'password', 'account', 'verify', 'update your information',
  'tax refund', 'irs', 'government', 'fbi', 'cia',
  'replica', 'rolex', 'luxury watches', 'designer',
  'seo', 'traffic', 'subscribers', 'followers', 'likes',
  'weight loss', 'diet', 'fat burn', 'slim', 'inches'
];

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Function to check if text contains spam keywords
const containsSpamKeywords = (text: string): boolean => {
  const lowerText = text.toLowerCase();
  return SPAM_KEYWORDS.some(keyword => lowerText.includes(keyword.toLowerCase()));
};

// Function to validate email format and domain
const isValidEmail = (email: string): boolean => {
  if (!EMAIL_REGEX.test(email)) return false;
  
  // Check for disposable email domains (simplified list)
  const disposableDomains = ['tempmail.com', 'temp-mail.org', 'guerrillamail.com', 'mailinator.com', 'yopmail.com'];
  const domain = email.split('@')[1].toLowerCase();
  if (disposableDomains.includes(domain)) return false;
  
  return true;
};

// Function to detect potential spam emails with enhanced patterns
const isSpamEmail = (email: string): boolean => {
  // Convert email to lowercase for consistent checking
  const lowerEmail = email.toLowerCase();
  
  // Check for common spam patterns
  const spamPatterns = [
    /scam/i,                   // Contains 'scam'
    /spam/i,                   // Contains 'spam'
    /test\d*@/i,               // test@, test1@, test123@, etc.
    /\d{6,}@/i,                // 6+ consecutive numbers before @
    /[a-z0-9]{15,}@/i,         // Very long username part (15+ chars)
    /temp\d*@/i,               // temp@, temp1@, etc.
    /fake/i,                   // Contains 'fake'
    /disposable/i,             // Contains 'disposable'
    /^[a-z]{1,2}\d{4,}@/i,     // Short letters followed by many numbers
    /^admin@/i,                // Starts with admin@
    /^info@/i,                 // Starts with info@
    /^user\d+@/i,              // user1@, user2@, etc.
    /^[a-z]+\d{3,}@/i,         // word followed by 3+ digits
    /^noreply@/i,              // Starts with noreply@
    /^donotreply@/i,           // Starts with donotreply@
    /^no-reply@/i,             // Starts with no-reply@
    /^[a-z]{1,3}@/i,           // Very short username (1-3 chars)
    /^[a-z]+[._-]?test\d*@/i,  // Anything ending with test@
    /^[a-z]+\.(test|fake)@/i,  // name.test@ or name.fake@
    /^[a-z]+\d*\.(scam|spam)@/i // name.scam@ or name.spam@
  ];
  
  // Check if the email matches any spam pattern
  return spamPatterns.some(pattern => pattern.test(lowerEmail));
};

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';

const Contact: React.FC = () => {
  // Initialize EmailJS with your public key
  useEffect(() => {
    emailjs.init("89prRQ72RL3SWmp0A");
    console.log('EmailJS initialized');
  }, []);

  // Initialize reCAPTCHA v3
  useEffect(() => {
    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = 'https://www.google.com/recaptcha/api.js?render=6LfVoAMrAAAAAMmbyqxcVEC4liIQxEIsbP8SEvaD';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      const script = document.getElementById(RECAPTCHA_SCRIPT_ID);
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, []);


  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '' // Honeypot field - should remain empty
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ success?: boolean; message?: string } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    // Get fresh reCAPTCHA v3 token
    let token;
    try {
      token = await window.grecaptcha.execute('6LfVoAMrAAAAAMmbyqxcVEC4liIQxEIsbP8SEvaD', { action: 'submit' });
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: 'reCAPTCHA verification failed. Please try again.'
      });
      setIsSubmitting(false);
      return;
    }

    if (!token) {
      setSubmitStatus({
        success: false,
        message: 'Please complete the reCAPTCHA verification.'
      });
      setIsSubmitting(false);
      return;
    }

    // Spam detection checks
    const spamChecks: string[] = [];
    
    // 1. Check if honeypot field is filled (bots often fill all fields)
    if (formData.website.length > 0) {
      console.log('Honeypot field filled - likely bot submission');
      spamChecks.push('Bot detection');
    }
    
    // 2. Check for suspicious email patterns using enhanced detection
    if (isSpamEmail(formData.email)) {
      console.log('Potential spam email detected:', formData.email);
      spamChecks.push('Suspicious email pattern detected');
    }
    
    // 2. Check email validity
    if (!isValidEmail(formData.email)) {
      console.log('Invalid email format or domain');
      spamChecks.push('Invalid email');
    }
    
    // 3. Check for spam keywords in subject and message
    if (containsSpamKeywords(formData.subject) || containsSpamKeywords(formData.message)) {
      console.log('Spam keywords detected in content');
      spamChecks.push('Suspicious content');
    }
    
    // Remove forced spam detection
    
    // Log the form data for debugging
    console.log('Form data being checked:', formData);
    
    // 4. Check for excessive length (spam often has very long messages)
    if (formData.message.length > 2000) {
      console.log('Message too long - potential spam');
      spamChecks.push('Message too long');
    }
    
    // 5. Check for links in message (many spam messages contain multiple links)
    const linkCount = (formData.message.match(/https?:\/\//g) || []).length;
    if (linkCount > 2) {
      console.log('Too many links in message');
      spamChecks.push('Too many links');
    }
    
    // If spam detected, show success message to user but don't actually send email
    if (spamChecks.length > 0) {
      console.log('Spam detected, not sending email. Reasons:', spamChecks);
      
      // Force a spam notification to your personal email
      console.log('SPAM DETECTED! Sending notification to personal email...');
      
      // Add a forced spam indicator to make debugging easier
      spamChecks.push('TEST: Forced notification');
      
      // Log additional debugging information first
      console.log('Original form data:', formData);
      console.log('Spam indicators triggered:', spamChecks);
      
      // Local storage approach removed
      
      // Store spam notification in localStorage instead of sending email
      try {
        console.log('Storing spam notification for later review...');
        
        // Create a simple message for the notification
        const spamMessage = `SPAM DETECTED ON BAND WEBSITE\n\nA potential spam message was blocked.\n\nSpam indicators: ${spamChecks.join(', ')}\n\nSubmission details:\nName: ${formData.name}\nEmail: ${formData.email}\nSubject: ${formData.subject}\n\nMessage was not forwarded for security reasons.\n\nSent at: ${new Date().toLocaleString()}`;
        
        // Store spam entry in localStorage
        const spamEntries = JSON.parse(localStorage.getItem('spam_entries') || '[]');
        spamEntries.push({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          indicators: spamChecks,
          date: new Date().toISOString(),
          message: spamMessage
        });
        
        // Keep only the most recent 50 entries to avoid localStorage size issues
        if (spamEntries.length > 50) {
          spamEntries.splice(0, spamEntries.length - 50);
        }
        
        localStorage.setItem('spam_entries', JSON.stringify(spamEntries));
        
        // Log success message
        console.log('Successfully stored spam notification in local storage');
        console.log('Current spam entries:', spamEntries);
        
        // Note for future implementation when EmailJS is fixed:
        // const serviceId = 'service_cokesllc-band1i';
        // const templateId = 'template_lo30n41';
        // emailjs.send(serviceId, templateId, { to_email: 'sskcokes@gmail.com', ... });
      } catch (error) {
        console.error('Error storing spam notification:', error);
      }
      
      // Still show success to the user to not alert spammers
      setSubmitStatus({
        success: true,
        message: 'Your message has been sent! We will get back to you soon.'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        website: ''
      });
      
      setIsSubmitting(false);
      return;
    }
    
    try {
      console.log('Attempting to send email...');
      // Using the correct IDs from your EmailJS dashboard
      const serviceId = 'service_cokesllc_band1i'; // Updated service ID
      const templateId = 'template_lo30n41';
      
      const templateParams = {
        to_name: 'SSK Band',
        to_email: 'sskcokes@gmail.com',
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        reply_to: formData.email,
        date: new Date().toLocaleString(),
        spam_check: 'Passed',
        'g-recaptcha-response': token // Include the reCAPTCHA token
      };
      
      console.log('Attempting to send with service:', serviceId, 'template:', templateId);
      try {
        const response = await emailjs.send(serviceId, templateId, templateParams);
        console.log('EmailJS Response:', response);
        
        if (response.status !== 200) {
          console.error('Email send failed with status:', response.status);
          throw new Error(`Failed to send email: Status ${response.status}`);
        }
      } catch (error: any) {
        console.error('EmailJS error:', error);
        const errorMessage = error?.message || 'Unknown error';
        throw new Error(`Failed to send email: ${errorMessage}`);
      }
      
      setSubmitStatus({
        success: true,
        message: 'Your message has been sent! We will get back to you soon.'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        website: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      let errorMessage = 'There was an error sending your message. Please try again later.';
      
      // More specific error message if available
      if (error instanceof Error) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setSubmitStatus({
        success: false,
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section id="contact" className="py-20 bg-black text-white" style={{backgroundColor: '#000', backgroundImage: 'linear-gradient(to bottom, #000, #0a1930)'}}>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">Get in Touch</h2>
          <div className="h-1 w-20 bg-blue-500 mx-auto"></div>
          <p className="mt-6 text-xl text-gray-300 max-w-3xl mx-auto">
            Want to book us for a performance or just say hello? Reach out to us!
          </p>
        </div>

        <div className="flex justify-center">
          <div className="lg:w-1/2">
            <div className="bg-gray-900 rounded-lg p-8 shadow-lg mb-8">
              <h3 className="text-2xl font-bold mb-6 text-blue-300">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-300">Email</p>
                    <p className="text-base text-gray-400">sskcokes@gmail.com</p>
                  </div>
                </div>

                <div className="pt-2">
                  <a
                    href="mailto:sskcokes@gmail.com"
                    className="inline-flex items-center justify-center rounded-full bg-purple-600 px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-purple-700"
                  >
                    Contact Us
                  </a>
                </div>

              </div>
            </div>


          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
